
import {
  Component,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild
} from '@angular/core';

import { BeesHeaderComponent } from '../bees-header/bees-header.component';
import { BeesFooterComponent } from '../bees-footer/bees-footer.component';

import { RouterLink } from '@angular/router';

import Swiper from 'swiper';
import {
  Navigation,
  Pagination,
  Autoplay,
  EffectFade
} from 'swiper/modules';

import 'owl.carousel';

declare var $: any;

Swiper.use([Navigation, Pagination, Autoplay, EffectFade]);

@Component({
  selector: 'app-page-home',
  standalone: true,
  imports: [
    BeesHeaderComponent,
    BeesFooterComponent,
    RouterLink
  ],
  templateUrl: './page-home.component.html',
  styleUrl: './page-home.component.scss'
})
export class PageHomeComponent implements AfterViewInit, OnDestroy {

  @ViewChild('productScroller')
  productScroller!: ElementRef<HTMLDivElement>;

  private initializationTimer: ReturnType<typeof setTimeout> | undefined;
  private swipers: Swiper[] = [];
  private owlCarousels: any[] = [];

  ngAfterViewInit(): void {
    this.initializationTimer = setTimeout(() => {
      this.initializeMainSwiper();
      this.initializeServicesCarousel();
      this.initializeProjectCarousel();
      this.initializeTestimonialCarousel();
      this.initializeTeamCarousel();
      this.initializeBrandCarousel();
    }, 1000);
  }

  // =========================================
  // MAIN SWIPER SLIDER
  // =========================================

  private initializeMainSwiper(): void {
    const slider = document.querySelector('.thm-swiper__slider');

    if (!slider) {
      return;
    }

    this.swipers.push(
      new Swiper('.thm-swiper__slider', {
        slidesPerView: 1,
        loop: true,
        effect: 'fade',

        autoplay: {
          delay: 5000,
          disableOnInteraction: false
        },

        pagination: {
          el: '#main-slider-pagination',
          clickable: true
        },

        navigation: {
          nextEl: '#main-slider__swiper-button-next',
          prevEl: '#main-slider__swiper-button-prev'
        }
      })
    );
  }

  // =========================================
  // SERVICES SLIDER
  // =========================================

  private initializeServicesCarousel(): void {
    this.initializeOwlCarousel('.services-two__carousel', {
      loop: true,
      margin: 30,
      nav: true,
      dots: false,
      autoplay: true,
      autoplayTimeout: 5000,
      smartSpeed: 1000,

      responsive: {
        0: { items: 1 },
        768: { items: 2 },
        1200: { items: 3 }
      }
    });
  }

  // =========================================
  // PROJECT SLIDER
  // =========================================

  private initializeProjectCarousel(): void {
    this.initializeOwlCarousel('.project-two__carousel', {
      loop: true,
      margin: 30,
      nav: true,
      dots: false,
      autoplay: true,
      autoplayTimeout: 5000,
      smartSpeed: 1000,

      responsive: {
        0: { items: 1 },
        768: { items: 2 },
        1200: { items: 4 }
      }
    });
  }

  // =========================================
  // TESTIMONIAL SLIDER
  // =========================================

  private initializeTestimonialCarousel(): void {
    this.initializeOwlCarousel('.testimonial-two__carousel', {
      loop: true,
      margin: 30,
      nav: true,
      dots: false,
      autoplay: true,
      autoplayTimeout: 5000,
      smartSpeed: 1000,

      responsive: {
        0: { items: 1 },
        768: { items: 2 },
        1200: { items: 3 }
      }
    });
  }

  // =========================================
  // TEAM SLIDER
  // =========================================

  private initializeTeamCarousel(): void {
    this.initializeOwlCarousel('.team-two__carousel', {
      loop: true,
      margin: 30,
      nav: true,
      dots: false,
      autoplay: true,
      autoplayTimeout: 5000,
      smartSpeed: 1000,

      responsive: {
        0: { items: 1 },
        768: { items: 2 },
        1200: { items: 4 }
      }
    });
  }

  // =========================================
  // BRAND SLIDER
  // =========================================

  private initializeBrandCarousel(): void {
    this.initializeOwlCarousel('.brand-one__carousel', {
      loop: true,
      margin: 30,
      nav: false,
      dots: false,
      autoplay: true,
      autoplayTimeout: 2000,
      smartSpeed: 1000,

      responsive: {
        0: { items: 2 },
        600: { items: 3 },
        1000: { items: 5 }
      }
    });
  }

  // =========================================
  // SHARED OWL CAROUSEL INITIALIZER
  // =========================================

  private initializeOwlCarousel(
    selector: string,
    options: Record<string, any>
  ): void {
    const elements = $(selector);

    if (!elements.length) {
      return;
    }

    elements.each((_index: number, element: HTMLElement) => {
      const carousel = $(element);

      // Prevent duplicate initialization
      if (carousel.hasClass('owl-loaded')) {
        return;
      }

      carousel.owlCarousel(options);
      this.owlCarousels.push(carousel);
    });
  }

  // =========================================
  // PRODUCT IMAGE CARD SCROLLING
  // =========================================

  scrollProducts(direction: number): void {
    const slider = this.productScroller?.nativeElement;

    if (!slider) {
      return;
    }

    const firstCard = slider.querySelector(
      '.product-card'
    ) as HTMLElement | null;

    const styles = window.getComputedStyle(slider);
    const gap = parseFloat(styles.columnGap || styles.gap) || 18;

    const scrollAmount = firstCard
      ? (firstCard.offsetWidth + gap) * 2
      : slider.clientWidth * 0.8;

    slider.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth'
    });
  }

  // Allow mouse-wheel scrolling to move horizontally on desktop
  onProductWheel(event: WheelEvent): void {
    const slider = this.productScroller?.nativeElement;

    if (!slider || window.innerWidth < 768) {
      return;
    }

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault();
      slider.scrollLeft += event.deltaY;
    }
  }

  // =========================================
  // CLEANUP
  // =========================================

  ngOnDestroy(): void {
    if (this.initializationTimer !== undefined) {
      clearTimeout(this.initializationTimer);
    }

    this.swipers.forEach((swiper) => {
      if (!swiper.destroyed) {
        swiper.destroy(true, true);
      }
    });

    this.owlCarousels.forEach((carousel) => {
      if (carousel.hasClass('owl-loaded')) {
        carousel.trigger('destroy.owl.carousel');
      }
    });

    this.owlCarousels = [];
    this.swipers = [];
  }
}
